import { FunctionComponent, useCallback, useEffect, useRef } from "react";

import { classNames } from "@/utils/classNames";

import { ContentStatus } from "../schema/knowledgeDb";
import { Product, PRODUCT_TYPE_LABELS, ProductType } from "../schema/productsServices";

import { ContentSection } from "./ContentSection";
import { EditableStringListTable } from "./EditableStringListTable";
import { TargetSegmentChips } from "./TargetSegmentChips";

interface ProductContentAreaProps {
	products: Product[];
	activeProductId: number | null;
	onProductChange: (updated: Product) => void;
	onProductDelete: (productId: number) => void;
	onProductApprove: (productId: number) => void;
	onProductUnapprove: (productId: number) => void;
}

const TYPE_BADGE_COLORS: Record<ProductType, string> = {
	[ProductType.PRODUCT]: "bg-blue-action-200 text-blue-action border border-blue-action-400",
	[ProductType.SERVICE]:
		"bg-status-success-light text-status-success border border-status-success-400",
	[ProductType.FRAMEWORK]:
		"bg-status-warning-light text-status-warning border border-status-warning-400",
};

export const ProductContentArea: FunctionComponent<ProductContentAreaProps> = ({
	products,
	activeProductId,
	onProductChange,
	onProductDelete,
	onProductApprove,
	onProductUnapprove,
}) => {
	const productRefs = useRef<Record<number, HTMLDivElement | null>>({});

	useEffect(() => {
		if (activeProductId && productRefs.current[activeProductId]) {
			productRefs.current[activeProductId]?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}
	}, [activeProductId]);

	const updateProduct = useCallback(
		(productId: number, partial: Partial<Product>) => {
			const product = products.find((p) => p.id === productId);

			if (product) {
				onProductChange({ ...product, ...partial });
			}
		},
		[products, onProductChange]
	);

	const toggleSegment = useCallback(
		(productId: number, segmentId: number) => {
			const product = products.find((p) => p.id === productId);

			if (product) {
				const updatedSegments = product.targetSegments.map((s) =>
					s.id === segmentId ? { ...s, isActive: !s.isActive } : s
				);

				onProductChange({ ...product, targetSegments: updatedSegments });
			}
		},
		[products, onProductChange]
	);

	if (products.length === 0) {
		return (
			<div className="flex-1 flex items-center justify-center text-sm text-gray-500 min-h-[400px]">
				No products or services added yet. Click &quot;+ Add Product/Service&quot; to get started.
			</div>
		);
	}

	return (
		<div className="flex-1 overflow-y-auto px-6">
			{products.map((product) => (
				<div
					key={product.id}
					ref={(el) => {
						productRefs.current[product.id] = el;
					}}
					className="mb-8"
				>
					{/* Sticky product header */}
					<div className="sticky top-0 z-10 bg-white pb-3 mb-4 border-b border-gray-200 py-4">
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-3">
								<h2 className="text-ssmd font-bold text-gray-900">{product.name}</h2>
								<span
									className={classNames(
										"inline-flex items-center px-2 py-0.5 rounded-xl text-xs font-medium",
										TYPE_BADGE_COLORS[product.type]
									)}
								>
									{PRODUCT_TYPE_LABELS[product.type]}
								</span>
								{product.status === ContentStatus.AI_DRAFTED && (
									<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-brand-white border border-blue-action/40 text-blue-action">
										✨ AI
									</span>
								)}
								{product.status === ContentStatus.DRAFT && (
									<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-status-warning-light border border-status-warning/40 text-status-warning">
										✎ Draft
									</span>
								)}
								{product.status === ContentStatus.HUMAN_APPROVED && (
									<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-status-success-light border border-status-success/40 text-status-success">
										✓ Approved
									</span>
								)}
							</div>
							<div className="flex items-center gap-2">
								{(product.status === ContentStatus.AI_DRAFTED ||
									product.status === ContentStatus.DRAFT) && (
									<button
										className="text-xs font-medium cursor-pointer text-status-warning hover:text-status-warning/80 px-2 py-1 rounded transition-colors"
										type="button"
										onClick={() => onProductApprove(product.id)}
									>
										✓ Approve
									</button>
								)}
								{product.status === ContentStatus.HUMAN_APPROVED && (
									<button
										className="text-xs font-medium cursor-pointer text-status-warning hover:text-status-warning/80 px-2 py-1 rounded transition-colors"
										type="button"
										onClick={() => onProductUnapprove(product.id)}
									>
										✗ Unapprove
									</button>
								)}
								<button
									className="text-xs font-medium cursor-pointer text-red-500 hover:text-red-500/80 px-2 py-1 rounded transition-colors"
									type="button"
									onClick={() => onProductDelete(product.id)}
								>
									Delete
								</button>
							</div>
						</div>
					</div>

					{/* Description */}
					<ContentSection
						collapsible
						contentStatus={product.status}
						editValue={product.description}
						title="Description"
						onApprove={() => onProductApprove(product.id)}
						onSave={(value) => updateProduct(product.id, { description: value })}
						onUnapprove={() => onProductUnapprove(product.id)}
					>
						{product.description || <span className="text-gray-400 italic">No description.</span>}
					</ContentSection>

					{/* Key Features */}
					<EditableStringListTable
						contentStatus={product.status}
						items={product.keyFeatures}
						title="Key Features / Capabilities"
						onApprove={() => onProductApprove(product.id)}
						onSave={(items) => updateProduct(product.id, { keyFeatures: items })}
						onUnapprove={() => onProductUnapprove(product.id)}
					/>

					{/* Target Segments */}
					<div className="rounded-xl border border-gray-300 bg-white mb-4">
						<div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
							<h3 className="text-sm font-semibold text-gray-900">Target Segments</h3>
						</div>
						<div className="px-4 py-3">
							<TargetSegmentChips
								segments={product.targetSegments}
								onToggle={(segmentId) => toggleSegment(product.id, segmentId)}
							/>
						</div>
					</div>

					{/* Competitive Differentiation */}
					<ContentSection
						collapsible
						contentStatus={product.status}
						editValue={product.competitiveDiff}
						title="Competitive Differentiation"
						onApprove={() => onProductApprove(product.id)}
						onSave={(value) => updateProduct(product.id, { competitiveDiff: value })}
						onUnapprove={() => onProductUnapprove(product.id)}
					>
						{product.competitiveDiff || (
							<span className="text-gray-400 italic">No differentiation data.</span>
						)}
					</ContentSection>

					{/* Pricing & Positioning */}
					<ContentSection
						collapsible
						contentStatus={product.status}
						editValue={product.pricing}
						title="Pricing & Positioning"
						onApprove={() => onProductApprove(product.id)}
						onSave={(value) => updateProduct(product.id, { pricing: value })}
						onUnapprove={() => onProductUnapprove(product.id)}
					>
						{product.pricing || <span className="text-gray-400 italic">No pricing data.</span>}
					</ContentSection>
				</div>
			))}
		</div>
	);
};
